import { FC, memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  ListItemText,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MoreHorizOutlined } from '@mui/icons-material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { RouteMap } from '@/routes/Routes';
import videoPlaceholder from '@/assets/placeholder-video.png';
import audioPlaceholder from '@/assets/placeholder-audio.png';
import { ExpandedListRow, ThumbnailBox, RowContent, TagsRow, ActionButtons, MoreButton } from './styles';
import { SmallerChip } from '@/components/Tag/styles';
import DeleteModal from '@/components/CustomModal/DeleteModal';
import { useToggleFavorite } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import useAlertStore from '@/stores/alertStore';
import type { SharedBy, NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { getTagName } from '@/utils/tagUtils';
import { logger } from '@/services/loggerService';
import { METADATA_COL } from '@/constants/noteListColumns';
import { formatOwnerName } from '@/helpers/ownerName';

const formatDuration = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) return '-';
  const totalSec = Math.round(seconds);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatBytes = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatShortDate = (dateString?: string): string => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const mon = MONTH_ABBR[d.getMonth()];
  const day = d.getDate();
  if (d.getFullYear() === now.getFullYear()) return `${mon} ${day}`;
  return `${mon} ${day}, '${String(d.getFullYear()).slice(2)}`;
};

// Small chip next to the title (Team badge, note owner).
const titleChipSx = {
  flexShrink: 0,
  height: 20,
  fontWeight: 600,
  '& .MuiChip-icon': { fontSize: 13, ml: '4px' },
  '& .MuiChip-label': { px: '6px', fontSize: 11 },
};

const cellSx = (width: number) => ({
  width,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  px: 1,
});

interface NoteListRowProps {
  id: string;
  title?: string | null;
  tags?: string[];
  transcript_url?: string | null;
  video_url?: string | null;
  created_at?: string;
  processing_type?: string | null;
  thumbnail_url?: string | null;
  creator_user_id: string;
  shared_by?: SharedBy | null;
  is_favorite?: boolean;
  summary?: string | null;
  metadata?: NoteOut['metadata'];
  showThumbnails: boolean;
  showTags: boolean;
  onDelete?: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  /** Open the team move/remove modal for this note (personal views only). */
  onMoveToTeam?: (id: string) => void;
  onFavoriteToggle?: (noteId: string, newValue: boolean) => void;
  /**
   * Persist the star somewhere other than the note itself.
   *
   * Default (omitted) writes `notes/{id}.is_favorite` via the API, which only
   * the note's owner is allowed to do. Watched Notes are other people's notes,
   * so that view passes a writer that stores the star on the viewer's own user
   * doc instead. The button, its states and `onFavoriteToggle` are unchanged
   * either way — only the destination differs.
   */
  saveFavorite?: (noteId: string, newValue: boolean) => Promise<void>;
  navigateUrl?: string;
  /** Show a "Team" badge — this note is shared into one of the user's teams. */
  isTeamNote?: boolean;
  hideFavorite?: boolean;
  menuActionLabel?: string;
  deleteModalTitle?: string;
  deleteModalText?: string;
  deleteButtonText?: string;
}

const NoteListRow: FC<NoteListRowProps> = ({
  id,
  title,
  tags,
  video_url,
  created_at,
  thumbnail_url,
  creator_user_id,
  shared_by,
  is_favorite,
  summary,
  metadata,
  showThumbnails,
  showTags,
  onDelete,
  onMoveToFolder,
  onMoveToTeam,
  onFavoriteToggle,
  saveFavorite,
  navigateUrl,
  isTeamNote,
  hideFavorite,
  menuActionLabel,
  deleteModalTitle,
  deleteModalText,
  deleteButtonText,
}) => {
  const { userToken, userData } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showMetadata = useMediaQuery(theme.breakpoints.up('lg'));
  const showAlert = useAlertStore((state) => state.showAlert);
  const { mutateAsync: toggleFavorite } = useToggleFavorite();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<string>(video_url ? videoPlaceholder : audioPlaceholder);

  const hasVideo = !!video_url;

  // Show who owns the note when it isn't the current user's (Watched, Private, Team).
  // Compare against the backend doc id, never the Firebase Auth uid.
  const isSharedNote = !!creator_user_id && !!userData?.id && creator_user_id !== userData.id;
  const ownerName = isSharedNote ? formatOwnerName(shared_by) : '';

  useEffect(() => {
    let cancelled = false;
    const fetchThumbnail = async () => {
      try {
        if (!hasVideo) {
          if (!cancelled) setThumbnail(audioPlaceholder);
          return;
        }
        if (!thumbnail_url) {
          if (!cancelled) setThumbnail(videoPlaceholder);
          return;
        }
        const secureUrl = await resolveMediaUrl(thumbnail_url);
        if (!cancelled && secureUrl) setThumbnail(secureUrl);
      } catch {
        if (!cancelled) setThumbnail(videoPlaceholder);
      }
    };
    if (showThumbnails) fetchThumbnail();
    return () => {
      cancelled = true;
    };
  }, [hasVideo, thumbnail_url, showThumbnails]);

  const handleNavigateToNote = () => {
    if (navigateUrl) {
      navigate(navigateUrl);
      return;
    }
    navigate(`${RouteMap.NOTE}/${id}`);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleOpenDeleteModal = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setIsModalOpen(true);
    setAnchorEl(null);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleConfirmDelete = () => {
    onDelete?.(id);
    setIsModalOpen(false);
  };

  const handleMoveToFolder = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    onMoveToFolder?.(id);
  };

  const handleMoveToTeam = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    onMoveToTeam?.(id);
  };

  const performFavoriteToggle = async () => {
    try {
      const newValue = !is_favorite;
      if (saveFavorite) {
        await saveFavorite(id, newValue);
      } else {
        await toggleFavorite({
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId: id },
          body: { is_favorite: newValue },
        });
      }
      onFavoriteToggle?.(id, newValue);
      showAlert(newValue ? 'Added to favorites' : 'Removed from favorites', 'success');
    } catch (err) {
      logger.warn('notes', 'Failed to toggle favorite status', err);
      showAlert('Something went wrong while updating favorite status. Try again later.', 'error');
    }
  };

  const handleToggleFavorite = (event: React.MouseEvent) => {
    event.stopPropagation();
    void performFavoriteToggle();
  };

  const handleMenuFavoriteToggle = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    void performFavoriteToggle();
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isMenuOpen}
      onClose={() => setAnchorEl(null)}
      sx={{ marginTop: '4px' }}
    >
      {isMobile && !hideFavorite && (
        <MenuItem dense onClick={handleMenuFavoriteToggle}>
          <ListItemText>{is_favorite ? 'Remove from favorites' : 'Add to favorites'}</ListItemText>
        </MenuItem>
      )}
      {onMoveToFolder && (
        <MenuItem dense onClick={handleMoveToFolder}>
          <ListItemText>Move to folder</ListItemText>
        </MenuItem>
      )}
      {onMoveToTeam && (
        <MenuItem dense onClick={handleMoveToTeam}>
          <ListItemText>{isTeamNote ? 'Move note' : 'Move to team'}</ListItemText>
        </MenuItem>
      )}
      {onDelete && (
        <MenuItem dense onClick={handleOpenDeleteModal}>
          <ListItemText>{menuActionLabel || 'Archive'}</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  const favoriteButton = (
    <IconButton
      size="small"
      onClick={handleToggleFavorite}
      aria-label={is_favorite ? 'Remove from favorites' : 'Add to favorites'}
      sx={{ padding: '4px', visibility: is_favorite ? 'visible' : 'inherit' }}
      className={is_favorite ? undefined : 'list-row-actions'}
    >
      {is_favorite ? <StarIcon fontSize="small" sx={{ color: '#FFD700' }} /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
  );

  const hasMenuItems = (isMobile && !hideFavorite) || !!onDelete || !!onMoveToFolder || !!onMoveToTeam;

  const actionButtons = hasMenuItems ? (
    <ActionButtons className="list-row-actions">
      <MoreButton size="small" onClick={handleOpenMenu}>
        <MoreHorizOutlined />
      </MoreButton>
    </ActionButtons>
  ) : null;

  // Metadata values
  const webm = metadata?.media?.webm;

  const metadataCells = showMetadata && (
    <>
      <Box sx={cellSx(METADATA_COL.DURATION)}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {webm?.duration !== undefined ? formatDuration(webm.duration) : '-'}
        </Typography>
      </Box>
      <Box sx={cellSx(METADATA_COL.RESOLUTION)}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {webm?.resolution || '-'}
        </Typography>
      </Box>
      <Box sx={cellSx(METADATA_COL.SIZE)}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {webm?.file_size ? formatBytes(webm.file_size) : '-'}
        </Typography>
      </Box>
    </>
  );

  const dateCell = (
    <Box sx={cellSx(METADATA_COL.DATE)}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {formatShortDate(created_at)}
      </Typography>
    </Box>
  );

  const starredCell = hideFavorite ? null : (
    <Box
      sx={{
        width: METADATA_COL.STARRED,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {favoriteButton}
    </Box>
  );

  const actionsCell = (
    <Box
      sx={{
        width: METADATA_COL.ACTIONS,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      {actionButtons}
    </Box>
  );

  return (
    <>
      <ExpandedListRow
        isActive={isMenuOpen || isModalOpen}
        hasThumbnail={showThumbnails}
        onClick={handleNavigateToNote}
      >
        {showThumbnails && (
          <ThumbnailBox>
            <img src={thumbnail} alt="Note preview" loading="lazy" />
          </ThumbnailBox>
        )}
        <RowContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                minWidth: 0,
                ...(isMobile
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }
                  : {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
              }}
            >
              {title || 'Untitled'}
            </Typography>
            {isTeamNote && (
              <Chip icon={<GroupsOutlinedIcon />} label="Team" size="small" color="primary" sx={titleChipSx} />
            )}
            {!isMobile && ownerName && (
              <Chip
                icon={<PersonOutlineIcon />}
                label={ownerName}
                size="small"
                color="primary"
                sx={{ ...titleChipSx, maxWidth: '40%' }}
              />
            )}
          </Box>
          {isMobile ? (
            <Typography variant="caption" color="text.secondary">
              {[ownerName, formatShortDate(created_at)].filter(Boolean).join(' · ')}
            </Typography>
          ) : (
            summary && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {summary}
              </Typography>
            )
          )}
          {showTags && tags && tags.length > 0 && (
            <TagsRow>
              {tags.map((tag, index) => {
                const tagName = getTagName(tag);
                return <SmallerChip key={`${index}-${tagName}`} label={tagName} size="small" />;
              })}
            </TagsRow>
          )}
        </RowContent>
        {isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 0.5 }}>{actionButtons}</Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 2 }}>
            {metadataCells}
            {dateCell}
            {starredCell}
            {actionsCell}
          </Box>
        )}
      </ExpandedListRow>
      {hasMenuItems && renderMenu}
      {onDelete && (
        <DeleteModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          title={deleteModalTitle || 'Archive note?'}
          text={deleteModalText || 'Are you sure you want to archive this note?'}
          confirmButtonText={deleteButtonText || 'Yes, archive'}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

export default memo(NoteListRow);
